import React from 'react';
import { Plus, Edit, Trash2, Search, Wallet } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { caixaService, eventoService } from '../services/api';

const Caixas = () => {
  const { data: caixas, loading, error, create, update, remove } = useCrud(caixaService, 'caixas');
  const { data: eventos } = useCrud(eventoService, 'eventos');
  const modal = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');

  const validationRules = {
    data_abertura: { required: true, label: 'Data de Abertura' },
    valor_inicial: { 
      required: true, 
      label: 'Valor Inicial',
      custom: (value) => {
        if (value && isNaN(parseFloat(value))) {
          return 'Valor deve ser um número válido';
        }
        return null;
      }
    },
    responsavel_abertura: { required: true, label: 'Responsável pela Abertura', minLength: 2, maxLength: 100 },
    status: { required: true, label: 'Status' },
    evento_id: { required: false, label: 'Evento' }
  };

  const form = useForm(
    { 
      data_abertura: new Date().toISOString().split('T')[0],
      valor_inicial: '',
      responsavel_abertura: '',
      status: 'aberto',
      evento_id: ''
    },
    validationRules
  );

  const filteredCaixas = caixas.filter(caixa =>
    caixa.responsavel_abertura?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    caixa.Evento?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    caixa.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    form.reset();
    modal.open({ mode: 'create', title: 'Nova Caixa' });
  };

  const handleEdit = (caixa) => {
    form.reset({
      data_abertura: caixa.data_abertura ? caixa.data_abertura.split('T')[0] : new Date().toISOString().split('T')[0],
      valor_inicial: caixa.valor_inicial || '',
      responsavel_abertura: caixa.responsavel_abertura || '',
      status: caixa.status || 'aberto',
      evento_id: caixa.evento_id || ''
    });
    modal.open({ mode: 'edit', title: 'Editar Caixa', id: caixa.id });
  };

  const handleDelete = async (caixa) => {
    if (window.confirm(`Deseja realmente excluir a caixa aberta por "${caixa.responsavel_abertura}"?`)) {
      const result = await remove(caixa.id);
      if (result.success) {
        // Success handled by the hook
      }
    }
  };

  const handleSubmit = async (values) => {
    // Limpar campos opcionais vazios
    const cleanedValues = { ...values };
    
    if (!cleanedValues.evento_id || cleanedValues.evento_id === '') {
      cleanedValues.evento_id = null;
    }

    const result = modal.data.mode === 'create' 
      ? await create(cleanedValues)
      : await update(modal.data.id, cleanedValues);

    if (result.success) {
      modal.close();
      form.reset();
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Caixas</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={20} />
          Nova Caixa
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar caixas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
          <div className="text-sm text-gray-500">
            {filteredCaixas.length} caixa(s) encontrada(s)
          </div>
        </div>
      </div>

      <div className="card">
        {filteredCaixas.length === 0 ? (
          <div className="empty-state">
            <Wallet className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhuma caixa encontrada</h3>
            <p className="mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando uma nova caixa.'}
            </p>
            {!searchTerm && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Nova Caixa
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data Abertura</th>
                  <th>Responsável</th>
                  <th>Evento</th>
                  <th>Valor Inicial</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredCaixas.map((caixa) => (
                  <tr key={caixa.id}>
                    <td className="font-medium">{new Date(caixa.data_abertura).toLocaleDateString('pt-BR')}</td>
                    <td>{caixa.responsavel_abertura}</td>
                    <td>{caixa.Evento?.nome || 'N/A'}</td>
                    <td>{formatCurrency(caixa.valor_inicial)}</td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs ${
                        caixa.status === 'aberto' ? 'bg-green-100 text-green-800' : 
                        caixa.status === 'fechado' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {caixa.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(caixa)}
                          className="btn btn-sm btn-secondary"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(caixa)}
                          className="btn btn-sm btn-danger"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">
                ×
              </button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Data de Abertura *</label>
                <input
                  type="date"
                  value={form.values.data_abertura}
                  onChange={(e) => form.setValue('data_abertura', e.target.value)}
                  onBlur={() => form.setTouched('data_abertura')}
                  className={`form-input ${form.errors.data_abertura && form.touched.data_abertura ? 'border-red-500' : ''}`}
                />
                {form.errors.data_abertura && form.touched.data_abertura && (
                  <div className="form-error">{form.errors.data_abertura}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Responsável pela Abertura *</label>
                <input
                  type="text"
                  value={form.values.responsavel_abertura}
                  onChange={(e) => form.setValue('responsavel_abertura', e.target.value)}
                  onBlur={() => form.setTouched('responsavel_abertura')}
                  className={`form-input ${form.errors.responsavel_abertura && form.touched.responsavel_abertura ? 'border-red-500' : ''}`}
                  placeholder="Digite o nome do responsável"
                />
                {form.errors.responsavel_abertura && form.touched.responsavel_abertura && (
                  <div className="form-error">{form.errors.responsavel_abertura}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Valor Inicial *</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.values.valor_inicial}
                  onChange={(e) => form.setValue('valor_inicial', e.target.value)}
                  onBlur={() => form.setTouched('valor_inicial')}
                  className={`form-input ${form.errors.valor_inicial && form.touched.valor_inicial ? 'border-red-500' : ''}`}
                  placeholder="0.00"
                />
                {form.errors.valor_inicial && form.touched.valor_inicial && (
                  <div className="form-error">{form.errors.valor_inicial}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Status *</label>
                <select
                  value={form.values.status}
                  onChange={(e) => form.setValue('status', e.target.value)}
                  onBlur={() => form.setTouched('status')}
                  className={`form-select ${form.errors.status && form.touched.status ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione o status</option>
                  <option value="aberto">Aberto</option>
                  <option value="fechado">Fechado</option>
                </select>
                {form.errors.status && form.touched.status && (
                  <div className="form-error">{form.errors.status}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Evento</label>
                <select
                  value={form.values.evento_id}
                  onChange={(e) => form.setValue('evento_id', e.target.value)}
                  onBlur={() => form.setTouched('evento_id')}
                  className={`form-select ${form.errors.evento_id && form.touched.evento_id ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione um evento</option>
                  {eventos.map((evento) => (
                    <option key={evento.id} value={evento.id}>
                      {evento.nome}
                    </option>
                  ))}
                </select>
                {form.errors.evento_id && form.touched.evento_id && (
                  <div className="form-error">{form.errors.evento_id}</div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Caixas;